/* eslint-disable @typescript-eslint/no-explicit-any */
export const PRODUKTLY_FEATURE_TAG = 'produktly';

type ProduktlyWindowType = Window & {
  Produktly?: any;
  ProduktlyQ?: unknown[][];
  sharedState?: { user?: { featureTags?: Record<string, unknown> } };
};

const getWindow = () => window as ProduktlyWindowType;

/**
 * Single source of truth for whether the current user should get Produktly.
 * Reads the feature tags published to shared state, which is what the orchestrator
 * uses to decide whether to load the Produktly SDK.
 */
const hasProduktlyFeatureTag = (featureTags?: Record<string, unknown> | null) => {
  const tags = featureTags ?? getWindow().sharedState?.user?.featureTags ?? {};

  return Object.prototype.hasOwnProperty.call(tags, PRODUKTLY_FEATURE_TAG);
};

/**
 * Creates the Produktly command queue stub if it does not exist yet.
 * Every call made on the stub is pushed to `window.ProduktlyQ` and replayed in order by the SDK once it loads.
 */
const ensureProduktlyStub = () => {
  const w = getWindow();

  w.Produktly =
    w.Produktly ||
    new Proxy(() => {}, {
      get(_, name) {
        return (...args: unknown[]) => {
          w.ProduktlyQ = (w.ProduktlyQ || []).concat([[name, args]]);
        };
      },
      apply(_, __, args) {
        w.ProduktlyQ = (w.ProduktlyQ || []).concat([['apply', args]]);
      },
    });

  return w.Produktly;
};

/**
 * Identifies the user in Produktly with a persistent ID.
 * Safe to call before the SDK script is loaded: the call is queued and replayed by the SDK on load,
 * so the user is identified before any survey is evaluated for an anonymous visitor.
 */
const identifyProduktlyUser = (userId: string | number | null | undefined, userData: Record<string, unknown> = {}) => {
  if (userId === null || userId === undefined || userId === '') {
    return false;
  }

  ensureProduktlyStub().identifyUser(String(userId), { ...userData });

  return true;
};

export default {
  hasProduktlyFeatureTag,
  ensureProduktlyStub,
  identifyProduktlyUser,
};
